import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { Box, Tooltip, Typography } from '@mui/material';
import { 
  Add, 
  EditOutlined as Edit,
  DeleteOutlined as Delete,
  VisibilityOutlined as View,
} from '@mui/icons-material';
import { PlusIcon, TrashIcon, PencilIcon, EyeIcon } from '@heroicons/react/24/outline';
// Utilities
import { formatEnum } from '../../../utils/string';
import RoleScopeEnum from '../../../enums/RoleScopeEnum';
import { useSnackbar } from "../../../utils/useSnackbar";
import { 
  setRoleType,
  setActionType,
  setSelectedRoleID,
} from "../../../features/roleStepper/roleStepperSlice";
import { getRoleAndPermission, deleteRolesWithPermissions } from '../../../utils/api/role_apis';
import { STATUS } from '../../../utils/api/sharedfunction';
import { MissingData } from '../../../enums/enumRegistry';

// Component
import ConfirmationModal from '../../Modal/ConfirmationModal';
import ExpandableCell from '../../datagrid/ExpandableCell/ExpandableCell';
import StepperModal from './stepper/StepperModal';
import RoleDisplayModal from './RolesDisplayModal';
import ActionMenu from '../../menu/ActionMenu';
import TagsComponent from '../../Tags';
import ChipComponent from '../../chips/ChipComponent/ChipComponent';
import { getRoleTypeVariant } from '../../chips/ChipComponent/chipVariants';
import TableComponent from '../../TableComponent';
import CustomFooter from '../../datagrid/paginationFooter/CustomFooter';
import RowActionMenu from '../../menu/RowActionMenu';
import { renderTableCell } from '../../../utils/commonfunctions';
import { getDeleteModalTitle, getDeleteConfirmMessage } from '../../../utils/uiText';

// Utility function to determine role type based on org_id
const getRoleType = (org_id) => {
  return org_id === null ? 'Default' : 'Organization';
};


const ProjectRole = () => {
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const selectedRoleID = useSelector((state) => state.roleStepper.selectedRoleID);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [roleForAction, setRoleForAction] = useState(null);
  const [rolesAndPermission, setRolesAndPermission] = useState([]);
  const [stepperOpen, setStepperOpen] = useState(false);
  const [isDisplaying, setIsDisplaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isMultiAction = selectedRows.length > 0;
  const orgId = useSelector((state) => state.auth.user.org_id);

  // Pagination state
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25
  });

  useEffect(() => {
    dispatch(setRoleType(RoleScopeEnum.PROJECT_LEVEL));
    fetchRolesAndPermission();
  }, []);

  // Fetch all roles and permissions with project scope
  const fetchRolesAndPermission = async () => {
    try {
      setIsLoading(true);
      const response = await getRoleAndPermission(RoleScopeEnum.PROJECT_LEVEL);
      
      if (response.status === STATUS.SUCCESS) {
        // Transform the data to match the expected format
        const transformedData = response.data.roles.map(role => ({
          id: role.id,
          role: role.name,
          capabilities: role?.capabilities?.map(capability => capability?.display_name).join(', '),
          org_id: role.org_id,
        }));
        setRolesAndPermission(transformedData);
      } else {
        showSnackbar(response.error || "Failed to fetch roles and permissions", "error");
        console.error("Failed to fetch roles and permissions:", response.error);
      }
    } catch (error) {
      showSnackbar("Failed to fetch roles and permissions", "error");
      console.error("Failed to fetch roles and permissions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDelete = async () => {
    try {
      setIsLoading(true);
      const roleIdsToDelete = isMultiAction ? selectedRows : [selectedRoleID];
      const response = await deleteRolesWithPermissions(roleIdsToDelete);
      
      if (response.status === STATUS.SUCCESS) {
        setIsDeleting(false);
        const snapbarText = (isMultiAction && selectedRows.length > 1) ? `${selectedRows.length} Role(s) deleted successfully` : `Role deleted successfully`;
        showSnackbar(response.message || snapbarText, 'success');
        
        // Refetch Models
        await fetchRolesAndPermission();
        setSelectedRows([]);
      } else {
        const snapbarText = (isMultiAction && selectedRows.length > 1) ? `${selectedRows.length} Role(s) deleted failed` : `Role deleted failed`;
        showSnackbar(response.error || snapbarText, 'error');
        console.error('Error deleting roles:', response.error);
      }
    } catch (error) {
      const snapbarText = (isMultiAction && selectedRows.length > 1) ? `${selectedRows.length} Role(s) deleted failed` : `Role deleted failed`;
      showSnackbar(snapbarText, 'error');
      console.error('Error deleting roles:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Header Action Functions handlers
  const handleAdd = () => {
    dispatch(setActionType('create'));
    setStepperOpen(true);
  };

  const handleDelete = () => {
    setIsDeleting(true);
  };

  // Row Action handlers 
  const handlePermissionView = (role) => {
    dispatch(setSelectedRoleID(role.id));
    setRoleForAction(role);
    setIsDisplaying(true);
  };

  const handleRoleEdit = (role) => {
    dispatch(setActionType('edit'));
    dispatch(setSelectedRoleID(role.id));
    setStepperOpen(true);
  };

  const handleRoleDelete = (role) => {
    dispatch(setSelectedRoleID(role.id));
    setIsDeleting(true);
  };

  // Header action buttons
  const headerActionButtons = [
    { 
      name: 'Add', 
      tooltip: 'Add New Role', 
      icon: <PlusIcon className="icon" />,
      action: handleAdd, 
      disabled: isLoading
    },
    { 
      name: 'Delete', 
      tooltip: 'Delete selected role(s)', 
      icon: <TrashIcon className="icon" />,
      action: handleDelete, 
      disabled: selectedRows.length === 0 || isLoading,
    },
  ];

  // Columns definition for the DataGrid
  const columns = [
    {
      field: '__check__',
      hideable: false,
      sortable: false,
      filterable: false,
      minWidth: 55,
      maxWidth: 55,
      resizable: false,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'role',
      headerName: 'Roles',
      minWidth: 200,
      flex: 1,
      renderCell: (params) => renderTableCell(params.value)
    },
    {
      field: 'capabilities',
      headerName: 'Permissions',
      flex: 1, 
      minWidth: 900, 
      maxWidth: 1000,
      renderCell: (params) => (
        <ExpandableCell
          value={params.row.capabilities ?? MissingData.NONE}
          maxLength={100}
        />
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      minWidth: 150,
      maxWidth: 170,
      valueGetter: (value, row) => {
        return getRoleType(row?.org_id);
      },
      renderCell: (params) => (
        <ChipComponent
          variant={getRoleTypeVariant(params.value)}
          size="small"
        >
          {params.value}
        </ChipComponent>
      ),
    },
    {
      field: 'Actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      hideable: false,
      disableColumnMenu: true,
      resizable: false,
      minWidth: 90,
      maxWidth: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const isEditableRole = params.row.org_id === orgId;
        const dynamicRowActions = [
          {
            name: 'View',
            tooltip: 'View Permissions',
            icon: <EyeIcon className="icon" />,
            action: () => handlePermissionView(params.row),
            disabled: false,
          },
          {
            name: 'Edit',
            tooltip: 'Edit Role',
            icon: <PencilIcon className="icon" />,
            action: () => handleRoleEdit(params.row),
            disabled: !isEditableRole,
          },
          {
            name: 'Delete',
            tooltip: 'Delete Role',
            icon: <TrashIcon className="icon" />,
            action: () => handleRoleDelete(params.row),
            disabled: !isEditableRole,
          },
        ];
  
        return <RowActionMenu 
          menuItems={dynamicRowActions} 
          rowData={params.row} 
          isDisabled={false}
        />;
      },
    },
  ];

  return (
    <>
      <TableComponent
        title="Roles"
        headerActionButtons={headerActionButtons}
        rows={rolesAndPermission}
        columns={columns}
        loading={isLoading}
        idField="id"
        isCheckbox={true}
        isRowSelection={true}
        handleRowSelection={(selectionModel) => setSelectedRows(selectionModel)}
        selectedRows={selectedRows}
        paginationModel={paginationModel}
        handleCustomPaginationPageChange={(event, page) => setPaginationModel({...paginationModel, page: page})}
        handleCustomPaginationPageSizeChange={(pageSize) => setPaginationModel({page: 0, pageSize: pageSize})}
        isCustomPagination={true}
        isColumnResizable={!isLoading}
        disableColumnMenu={true}
        getRowHeight={() => 'auto'}
        getEstimatedRowHeight={() => 100}
        hideColumns={['role']}
      />

      {/* Create/Edit Role Modal */}
      <StepperModal
        open={stepperOpen}
        onClose={() => setStepperOpen(false)}
        onConfirm={() => {fetchRolesAndPermission()}}
      />

      {/* Display Role Modal */}
      <RoleDisplayModal
        name='Role & Permissions'
        open={isDisplaying}
        onClose={() => setIsDisplaying(false)}
        roleData={roleForAction}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={isDeleting}
        onClose={() => setIsDeleting(false)}
        onConfirm={confirmDelete}
        title={getDeleteModalTitle()}
        message={getDeleteConfirmMessage('role', selectedRows.length, isMultiAction)}
      />
    </>
  );
};

export default ProjectRole;
